import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSubscription } from '../hooks/useSubscription';
import UpgradePrompt from '../components/UpgradePrompt';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005';

const OCCASIONS = [
  { id: 'birthday', name: 'Birthday', icon: '🎂', color: 'from-pink-500 to-rose-500' },
  { id: 'anniversary', name: 'Anniversary', icon: '💕', color: 'from-red-500 to-pink-500' },
  { id: 'feeling_down', name: "When You're Feeling Down", icon: '💙', color: 'from-blue-500 to-indigo-500' },
  { id: 'congratulations', name: 'Congratulations', icon: '🎉', color: 'from-yellow-500 to-orange-500' },
  { id: 'miss_you', name: 'I Miss You Too', icon: '💌', color: 'from-purple-500 to-pink-500' },
  { id: 'proud', name: "I'm Proud of You", icon: '⭐', color: 'from-amber-500 to-yellow-500' },
  { id: 'courage', name: 'When You Need Courage', icon: '💪', color: 'from-orange-500 to-red-500' },
  { id: 'love', name: 'I Love You', icon: '❤️', color: 'from-red-500 to-rose-500' },
  { id: 'just_because', name: 'Just Because', icon: '✨', color: 'from-indigo-500 to-purple-500' },
  { id: 'custom', name: 'Custom Occasion', icon: '📝', color: 'from-gray-500 to-slate-500' }
];

export default function MessageCreate() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { subscription, usage, hasReachedLimit, checkLimit, incrementUsage } = useSubscription();
  
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [selectedOccasion, setSelectedOccasion] = useState('');
  const [customOccasion, setCustomOccasion] = useState('');
  const [messageText, setMessageText] = useState('');
  const [language, setLanguage] = useState('en');
  const [supportedLanguages, setSupportedLanguages] = useState({});
  const [generating, setGenerating] = useState(false);
  const [previewAudio, setPreviewAudio] = useState(null);
  const [saved, setSaved] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeLimitType, setUpgradeLimitType] = useState('messages');

  useEffect(() => {
    loadRecipients();
  }, [user]);

  const loadRecipients = async () => {
    try {
      const response = await axios.get(`${API_URL}/recipients/user/${user.id}`);
      setRecipients(response.data.recipients || []);
    } catch (error) {
      console.error('Failed to load recipients:', error);
    }
  };

  const loadLanguagePreference = async () => {
    try {
      const response = await axios.get(`${API_URL}/user/${user.id}/language-preference`);
      setLanguage(response.data.language_code || 'en');
    } catch (error) {
      console.error('Failed to load language preference:', error);
    }
  };

  const loadSupportedLanguages = async () => {
    try {
      const response = await axios.get(`${API_URL}/languages/supported`);
      setSupportedLanguages(response.data.languages || {});
    } catch (error) {
      console.error('Failed to load supported languages:', error);
    }
  };

  const saveLanguagePreference = async (langCode) => {
    try {
      await axios.post(`${API_URL}/user/language-preference`, {
        user_id: user.id,
        language_code: langCode
      });
    } catch (error) {
      console.error('Failed to save language preference:', error);
    }
  };

  const generatePreview = async () => {
    if (!messageText.trim() || !selectedRecipient) {
      alert('Please select a recipient and write a message');
      return;
    }

    // Check message limit before generating
    const limitCheck = await checkLimit('messages');
    if (!limitCheck.allowed) {
      setUpgradeLimitType('messages');
      setShowUpgradePrompt(true);
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(`${API_URL}/messages/generate`, {
        user_id: user.id,
        text: messageText,
        voice_id: user.voice_id || 'preview_voice',
        language_code: language
      });

      setPreviewAudio(response.data.audio_url);
    } catch (error) {
      console.error('Failed to generate preview:', error);
      alert('Failed to generate preview: ' + error.message);
    } finally {
      setGenerating(false);
    }
  };

  const saveMessage = async () => {
    if (!previewAudio) {
      alert('Please generate a preview first');
      return;
    }

    // Check WhatsApp limit before saving (since messages are delivered via WhatsApp)
    const whatsappCheck = await checkLimit('whatsapp');
    if (!whatsappCheck.allowed) {
      setUpgradeLimitType('whatsapp');
      setShowUpgradePrompt(true);
      return;
    }

    try {
      const occasion = selectedOccasion === 'custom' ? customOccasion : selectedOccasion;
      
      await axios.post(`${API_URL}/messages/save`, {
        creator_id: user.id,
        recipient_id: selectedRecipient,
        occasion: occasion,
        text: messageText,
        audio_url: previewAudio
      });

      // Increment usage counters
      await incrementUsage('messages', 1);
      await incrementUsage('whatsapp', 1);

      setSaved(true);
      setTimeout(() => {
        navigate('/messages');
      }, 2000);
    } catch (error) {
      console.error('Failed to save message:', error);
      alert('Failed to save message: ' + error.message);
    }
  };

  const selectedOccasionData = OCCASIONS.find(o => o.id === selectedOccasion);

  if (saved) {
    return (
      <div className="max-w-2xl mx-auto text-center py-20">
        <div className="bg-white rounded-2xl shadow-xl p-12">
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">✓</span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Message Saved!</h2>
          <p className="text-gray-600">
            Your message has been added to {recipients.find(r => r.id === selectedRecipient)?.name}'s portal
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Upgrade Prompt Modal */}
      <UpgradePrompt
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        limitType={upgradeLimitType}
        currentTier={subscription?.tier || 'free'}
        currentUsage={usage?.usage?.[upgradeLimitType]?.current || 0}
        limit={usage?.usage?.[upgradeLimitType]?.limit || 2}
      />

      {/* Subscription Usage Banner */}
      {usage && (
        <div className="mb-6 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                Current Plan: <span className="text-purple-600 capitalize">{subscription?.tier || 'Free'}</span>
              </h3>
            </div>
            {(hasReachedLimit('messages') || hasReachedLimit('whatsapp')) && (
              <button
                onClick={() => setShowUpgradePrompt(true)}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold rounded-lg hover:shadow-lg transition-all"
              >
                Upgrade Now
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-lg p-3">
              <p className="text-xs text-gray-500">Messages This Month</p>
              <p className="text-lg font-semibold text-gray-900">
                {usage.usage.messages.current}/{usage.usage.messages.limit === -1 ? '∞' : usage.usage.messages.limit}
              </p>
              {usage.usage.messages.limit !== -1 && (
                <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full ${
                      usage.usage.messages.percentage >= 100
                        ? 'bg-red-500'
                        : usage.usage.messages.percentage >= 80
                        ? 'bg-yellow-500'
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(100, usage.usage.messages.percentage)}%` }}
                  />
                </div>
              )}
            </div>
            <div className="bg-white rounded-lg p-3">
              <p className="text-xs text-gray-500">WhatsApp Sent</p>
              <p className="text-lg font-semibold text-gray-900">
                {usage.usage.whatsapp.current}/{usage.usage.whatsapp.limit === -1 ? '∞' : usage.usage.whatsapp.limit}
              </p>
              {usage.usage.whatsapp.limit !== -1 && (
                <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full ${
                      usage.usage.whatsapp.percentage >= 100
                        ? 'bg-red-500'
                        : usage.usage.whatsapp.percentage >= 80
                        ? 'bg-yellow-500'
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(100, usage.usage.whatsapp.percentage)}%` }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Create a Message</h1>
        <p className="text-gray-600">
          Write a message that will be converted to speech in your voice
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-xl p-8 space-y-8">
        
        {/* Select Recipient */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Who is this message for?
          </label>
          <select
            value={selectedRecipient}
            onChange={(e) => setSelectedRecipient(e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
          >
            <option value="">Select a family member...</option>
            {recipients.map(recipient => (
              <option key={recipient.id} value={recipient.id}>
                {recipient.name} ({recipient.relationship})
              </option>
            ))}
          </select>
        </div>

        {/* Select Occasion */}
        {selectedRecipient && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              What's the occasion?
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {OCCASIONS.map(occasion => (
                <button
                  key={occasion.id}
                  onClick={() => setSelectedOccasion(occasion.id)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    selectedOccasion === occasion.id
                      ? 'border-primary-500 bg-primary-50 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  <div className="text-3xl mb-2">{occasion.icon}</div>
                  <div className="text-sm font-medium text-gray-900">{occasion.name}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Custom Occasion Input */}
        {selectedOccasion === 'custom' && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Custom Occasion Name
            </label>
            <input
              type="text"
              value={customOccasion}
              onChange={(e) => setCustomOccasion(e.target.value)}
              placeholder="e.g., First Day of School"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
            />
          </div>
        )}

        {/* Message Text */}
        {selectedOccasion && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Your Message
            </label>
            <textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Write your message here... It will be converted to speech in your voice."
              rows={8}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-200 resize-none"
            />
            <p className="text-sm text-gray-500 mt-2">
              {messageText.length} characters
            </p>
          </div>
        )}

        {/* Preview Audio */}
        {previewAudio && (
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
            <p className="text-sm font-semibold text-green-900 mb-3">Preview Your Message:</p>
            <audio controls src={previewAudio} className="w-full mb-3" />
            <p className="text-xs text-green-700">
              This is how your message will sound to {recipients.find(r => r.id === selectedRecipient)?.name}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex space-x-4 pt-4">
          <button
            onClick={generatePreview}
            disabled={!messageText.trim() || generating}
            className="flex-1 px-6 py-4 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all"
          >
            {generating ? 'Generating...' : previewAudio ? 'Regenerate Preview' : 'Generate Preview'}
          </button>
          
          {previewAudio && (
            <button
              onClick={saveMessage}
              className="flex-1 px-6 py-4 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-all"
            >
              Save Message
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
